import { useState, useEffect } from 'react';
import { useDashboard, useInvoices, useLosses, useOrders, useBudget } from '../../hooks/useData';
import { api, getCurrentMonth, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, AlertTriangle, XCircle, Ban, Activity, Receipt, Trash2, ShoppingCart, Wallet, Brain, Loader2, CalendarDays, ChefHat, Wine, Package, ChevronLeft, ChevronRight, History, Percent } from 'lucide-react';

const MONTHS_FR = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const ChefBudget = () => {
  const [mois, setMois] = useState(getCurrentMonth());
  const { data: dashboard, loading, refetch } = useDashboard(mois);
  const { budget } = useBudget(mois);
  const { invoices } = useInvoices(mois);
  const { losses } = useLosses(mois);
  const { orders } = useOrders(mois);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [budgetHistory, setBudgetHistory] = useState([]);
  const [ratioData, setRatioData] = useState(null);

  useEffect(() => {
    api.get(`/budget/${mois}/historique`).then(r => setBudgetHistory(r.data)).catch(() => {});
    api.get(`/ratio-matieres/${mois}`).then(r => setRatioData(r.data)).catch(() => setRatioData(null));
  }, [mois]);

  const navigateMonth = (dir) => {
    const [yy, mm] = mois.split('-').map(Number);
    let nm = mm + dir, ny = yy;
    if (nm > 12) { nm = 1; ny++; }
    if (nm < 1) { nm = 12; ny--; }
    setMois(`${ny}-${String(nm).padStart(2, '0')}`);
  };

  const [yy, mm] = mois.split('-').map(Number);

  const budgetCuisine = budget?.budget_cuisine || 0;
  const totalFactures = invoices.reduce((sum, i) => {
    const detail = (i.cuisine_ht || 0) + (i.boissons_ht || 0) + (i.non_alimentaire_ht || 0);
    const invoiceTotal = detail > 0 ? detail : (i.total_ht || (i.total_ttc ? i.total_ttc / 1.1 : 0));
    return sum + invoiceTotal;
  }, 0);
  const totalPertes = losses.reduce((sum, l) => sum + (l.montant || 0), 0);
  const totalCommandes = orders.reduce((sum, o) => sum + (o.montant || 0), 0);
  const depense = totalFactures + totalPertes + totalCommandes;
  const budgetRestant = budgetCuisine - depense;
  const percentUsed = budgetCuisine > 0 ? (depense / budgetCuisine) * 100 : 0;

  // Jours restants dans le mois
  const now = new Date();
  const lastDay = new Date(yy, mm, 0).getDate();
  const isCurrentMonth = now.getFullYear() === yy && now.getMonth() + 1 === mm;
  const joursRestants = isCurrentMonth ? Math.max(1, lastDay - now.getDate() + 1) : lastDay;
  const budgetParJour = budgetRestant > 0 ? budgetRestant / joursRestants : 0;

  // Catégories
  const cuisineHT = invoices.reduce((sum, i) => sum + (i.cuisine_ht || 0), 0);
  const boissonsHT = invoices.reduce((sum, i) => sum + (i.boissons_ht || 0), 0);
  const nonAlimHT = invoices.reduce((sum, i) => sum + (i.non_alimentaire_ht || 0), 0);

  const runAI = async () => {
    setAiLoading(true);
    try {
      const res = await api.post('/optimize/achats');
      setAiResult(res.data.analysis);
    } catch { toast.error('Erreur IA'); }
    setAiLoading(false);
  };

  const getStatus = () => {
    if (percentUsed > 100) return { icon: Ban, color: 'rgb(var(--lm-red))', bg: 'bg-[rgb(var(--lm-red)/0.08)]', border: 'border-[rgb(var(--lm-red)/0.3)]', msg: 'Budget depassé', barColor: 'rgb(var(--lm-red))' };
    if (percentUsed >= 80) return { icon: XCircle, color: 'rgb(var(--lm-orange))', bg: 'bg-[rgb(var(--lm-orange)/0.06)]', border: 'border-[rgb(var(--lm-orange)/0.3)]', msg: 'Attention — Bientot la limite', barColor: 'rgb(var(--lm-orange))' };
    return { icon: CheckCircle, color: 'rgb(var(--lm-green))', bg: 'bg-[rgb(var(--lm-green)/0.06)]', border: 'border-[rgb(var(--lm-green)/0.3)]', msg: 'Dans les clous', barColor: 'rgb(var(--lm-green))' };
  };

  const st = getStatus();
  const StatusIcon = st.icon;

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 gap-4">
      <Activity size={28} className="animate-pulse-slow" style={{ color: 'rgb(var(--lm-green))' }} />
      <span className="text-xs tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">Chargement</span>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="chef-budget">
      {/* Header + Month Nav */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[rgb(var(--lm-text))] flex items-center gap-2">
          <ChefHat size={20} className="text-[rgb(var(--lm-green))]" /> Budget Courses
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => navigateMonth(-1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]" data-testid="chef-month-prev">
            <ChevronLeft size={16} className="text-[rgb(var(--lm-text-muted))]" />
          </button>
          <span className="text-sm font-medium text-[rgb(var(--lm-text))] min-w-[120px] text-center">
            {MONTHS_FR[mm]} {yy}
          </span>
          <button onClick={() => navigateMonth(1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]" data-testid="chef-month-next">
            <ChevronRight size={16} className="text-[rgb(var(--lm-text-muted))]" />
          </button>
        </div>
      </div>

      {/* No budget warning */}
      {budgetCuisine === 0 && (
        <div className="lm-card p-5 border-amber-500/30 bg-amber-500/5 text-center animate-slide-up" data-testid="no-budget-warning">
          <AlertTriangle size={24} className="mx-auto mb-2 text-amber-400" />
          <p className="font-medium text-sm text-amber-400">Aucun budget transmis</p>
          <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">Demandez au patron de transmettre le budget mensuel</p>
        </div>
      )}

      {/* Main Budget Card */}
      {budgetCuisine > 0 && (
        <div className={`lm-card p-6 ${st.border} ${st.bg} animate-slide-up`} data-testid="budget-main-card">
          {/* Budget Restant */}
          <div className="text-center mb-5">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Budget restant HT</p>
            <p className="font-mono text-5xl font-medium" style={{ color: st.color }} data-testid="budget-restant">
              {formatCurrency(budgetRestant)}
            </p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <StatusIcon size={16} style={{ color: st.color }} />
              <p className="text-sm" style={{ color: st.color }}>{st.msg}</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-[10px] text-[rgb(var(--lm-text-muted))] mb-1.5">
              <span>Depense</span>
              <span className="font-mono">{percentUsed.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-[rgb(var(--lm-surface-elevated))] rounded-full h-3 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700 ease-out" style={{
                width: `${Math.min(100, percentUsed)}%`,
                background: st.barColor,
              }} />
            </div>
            <div className="flex justify-between text-[10px] text-[rgb(var(--lm-text-muted))] mt-1 font-mono">
              <span>{formatCurrency(depense)}</span>
              <span>{formatCurrency(budgetCuisine)}</span>
            </div>
          </div>

          {/* Budget par jour restant */}
          {budgetRestant > 0 && isCurrentMonth && (
            <div className="text-center p-3 rounded-lg bg-[rgb(var(--lm-surface)/0.5)]" data-testid="budget-par-jour">
              <CalendarDays size={14} className="inline mr-1.5 text-[rgb(var(--lm-text-muted))]" />
              <span className="text-sm font-mono" style={{ color: st.color }}>
                ~{formatCurrency(budgetParJour)} / jour
              </span>
              <span className="text-xs text-[rgb(var(--lm-text-muted))] ml-2">
                ({joursRestants}j restants)
              </span>
            </div>
          )}
        </div>
      )}

      {/* Ratio Matieres simplifié */}
      {ratioData && ratioData.ca_ht > 0 && (
        <div className="lm-card p-4 animate-slide-up" style={{ animationDelay: '30ms', animationFillMode: 'backwards' }} data-testid="chef-ratio-matieres">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Percent size={14} style={{ color: ratioData.ratio_global <= 30 ? 'rgb(var(--lm-green))' : ratioData.ratio_global <= 35 ? 'rgb(var(--lm-orange))' : 'rgb(var(--lm-red))' }} />
              <span className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Ratio Achats / CA</span>
            </div>
            <span className={`font-mono text-xl font-bold ${
              ratioData.ratio_global <= 30 ? 'text-[rgb(var(--lm-green))]' : ratioData.ratio_global <= 35 ? 'text-[rgb(var(--lm-orange))]' : 'text-[rgb(var(--lm-red))]'
            }`}>{ratioData.ratio_global}%</span>
          </div>
          <div className="w-full bg-[rgb(var(--lm-surface-elevated))] rounded-full h-2 overflow-hidden mb-2">
            <div className="h-full rounded-full transition-all duration-700" style={{
              width: `${Math.min(100, ratioData.ratio_global * 2)}%`,
              background: ratioData.ratio_global <= 30 ? 'rgb(var(--lm-green))' : ratioData.ratio_global <= 35 ? 'rgb(var(--lm-orange))' : 'rgb(var(--lm-red))'
            }} />
          </div>
          <div className="flex justify-between text-[10px] text-[rgb(var(--lm-text-muted))]">
            <span>Achats: {formatCurrency(ratioData.achats_total_ht)} HT</span>
            <span>CA: {formatCurrency(ratioData.ca_ht)} HT</span>
          </div>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1">
            Objectif: {ratioData.ratio_objectif}% — {ratioData.ratio_global <= ratioData.ratio_objectif ? 'Dans les clous' : `Depasse de ${(ratioData.ratio_global - ratioData.ratio_objectif).toFixed(1)} pts`}
          </p>
        </div>
      )}

      {/* Détail des dépenses */}
      {budgetCuisine > 0 && (
        <div className="space-y-2 animate-slide-up" style={{ animationDelay: '60ms', animationFillMode: 'backwards' }}>
          <div className="lm-card p-3 flex justify-between items-center" style={{ borderColor: 'rgb(var(--lm-green) / 0.2)' }}>
            <div className="flex items-center gap-2">
              <Wallet size={14} style={{ color: 'rgb(var(--lm-green))' }} />
              <span className="text-sm">Budget</span>
            </div>
            <span className="font-mono text-lg" style={{ color: 'rgb(var(--lm-green))' }}>{formatCurrency(budgetCuisine)}</span>
          </div>
          <div className="lm-card p-3 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Receipt size={14} className="text-[rgb(var(--lm-text-muted))]" />
              <span className="text-sm">Achats ({invoices.length})</span>
            </div>
            <span className="font-mono">- {formatCurrency(totalFactures)}</span>
          </div>
          {totalPertes > 0 && (
            <div className="lm-card p-3 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Trash2 size={14} style={{ color: 'rgb(var(--lm-red))' }} />
                <span className="text-sm">Pertes ({losses.length})</span>
              </div>
              <span className="font-mono" style={{ color: 'rgb(var(--lm-red))' }}>- {formatCurrency(totalPertes)}</span>
            </div>
          )}
          {totalCommandes > 0 && (
            <div className="lm-card p-3 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <ShoppingCart size={14} className="text-[rgb(var(--lm-text-muted))]" />
                <span className="text-sm">Commandes ({orders.length})</span>
              </div>
              <span className="font-mono">- {formatCurrency(totalCommandes)}</span>
            </div>
          )}
        </div>
      )}

      {/* Répartition catégories */}
      {totalFactures > 0 && (
        <div className="lm-card p-4 animate-slide-up" style={{ animationDelay: '100ms', animationFillMode: 'backwards' }}>
          <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Repartition</h3>
          <div className="space-y-2">
            {[
              { label: 'Cuisine', value: cuisineHT, icon: ChefHat, color: 'rgb(var(--lm-green))' },
              { label: 'Boissons', value: boissonsHT, icon: Wine, color: 'rgb(100, 149, 237)' },
              { label: 'Non-alim', value: nonAlimHT, icon: Package, color: 'rgb(var(--lm-orange))' },
            ].filter(c => c.value > 0).map(cat => {
              const pct = totalFactures > 0 ? (cat.value / totalFactures * 100) : 0;
              return (
                <div key={cat.label} className="flex items-center gap-3">
                  <cat.icon size={14} style={{ color: cat.color }} className="shrink-0" />
                  <div className="flex-1">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-[rgb(var(--lm-text-secondary))]">{cat.label}</span>
                      <span className="font-mono">{formatCurrency(cat.value)} ({pct.toFixed(0)}%)</span>
                    </div>
                    <div className="h-1.5 bg-[rgb(var(--lm-surface-elevated))] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, background: cat.color }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Budget History */}
      {budgetHistory.length > 0 && (
        <div className="lm-card p-4 animate-slide-up" style={{ animationDelay: '120ms', animationFillMode: 'backwards' }} data-testid="budget-history">
          <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Evolution du budget</h3>
          <div className="space-y-2">
            {budgetHistory.map((h, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-[rgb(var(--lm-text-muted))] font-mono text-xs">{new Date(h.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-[rgb(var(--lm-text-secondary))]">{formatCurrency(h.ancien)}</span>
                  <span className="text-[rgb(var(--lm-text-muted))]">&rarr;</span>
                  <span className="font-mono text-xs text-[rgb(var(--lm-text))]">{formatCurrency(h.nouveau)}</span>
                  <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-full ${h.ecart > 0 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                    {h.ecart > 0 ? '+' : ''}{formatCurrency(h.ecart)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI */}
      <div className="lm-card p-4 animate-slide-up" style={{ animationDelay: '140ms', animationFillMode: 'backwards' }}>
        <Button onClick={runAI} disabled={aiLoading} className="w-full bg-lemas-gold text-lemas-bg hover:bg-lemas-gold/90" data-testid="ai-optimize-btn">
          {aiLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Brain size={16} className="mr-2" />}
          Comment economiser ?
        </Button>
        {aiResult && (
          <div className="mt-4 p-3 rounded-lg bg-lemas-bg/60 text-sm text-[rgb(var(--lm-text-secondary))] whitespace-pre-wrap leading-relaxed" data-testid="ai-result">
            {aiResult}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChefBudget;
